import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Mail, Phone, User2, BadgeCheck, CalendarClock, KeyRound } from "lucide-react";

/**
 * Account — Iteration 31
 * Lightweight profile/info page surfaced from the navbar dropdown.
 * Read-only for now: displays the customer's name, email, phone, role,
 * and join date (if known). Includes a shortcut to the password-reset flow.
 */
const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ar-SA", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

const Row = ({ icon: Icon, label, value, dir }) => (
  <div className="flex items-start gap-4 py-4 border-b border-[#F0EDE4] last:border-b-0">
    <div className="shrink-0 w-9 h-9 rounded-full bg-[#F6F4EE] grid place-items-center">
      <Icon className="w-4 h-4 text-[#0A4D3C]" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-xs text-[#8B8780]">{label}</div>
      <div
        className="mt-0.5 text-sm text-[#222222] break-words"
        dir={dir || "rtl"}
        data-testid={`account-${label}`}
      >
        {value || <span className="text-[#8B8780]">—</span>}
      </div>
    </div>
  </div>
);

const Account = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  if (!loading && !user) {
    navigate("/login");
    return null;
  }
  if (!user) return null;

  const fullName =
    [user.first_name, user.middle_name, user.last_name].filter(Boolean).join(" ") ||
    user.name ||
    "—";

  return (
    <PageShell>
      <section
        className="max-w-3xl mx-auto px-6 py-12"
        data-testid="account-page"
        dir="rtl"
      >
        <div className="text-center">
          <div className="inline-flex w-16 h-16 rounded-full bg-[#0A4D3C] text-white items-center justify-center font-heading text-2xl">
            {(user.name || "ا").trim().charAt(0)}
          </div>
          <h1 className="font-heading text-3xl text-[#222222] mt-4">معلومات الحساب</h1>
          <p className="text-[#5E5C58] mt-2 text-sm">
            البيانات المسجّلة حالياً في حسابك. تواصل معنا لأي تعديل.
          </p>
        </div>

        <div className="mt-8 rounded-2xl border border-[#E5E1D8] bg-white px-6 py-2 shadow-[0_2px_24px_-18px_rgba(60,40,20,0.25)]">
          <Row icon={User2} label="الاسم" value={fullName} />
          <Row icon={Mail} label="البريد الإلكتروني" value={user.email} dir="ltr" />
          <Row icon={Phone} label="رقم الجوال" value={user.phone} dir="ltr" />
          <Row
            icon={BadgeCheck}
            label="نوع الحساب"
            value={user.role === "admin" ? "مسؤول" : "عميل"}
          />
          <Row icon={CalendarClock} label="تاريخ التسجيل" value={fmt(user.created_at)} />
        </div>

        <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <Button
            data-testid="account-go-dashboard"
            variant="outline"
            className="rounded-full flex-1"
            onClick={() => navigate("/dashboard")}
          >
            حسابي وحجوزاتي
          </Button>
          <Button
            data-testid="account-reset-password"
            variant="outline"
            className="rounded-full flex-1"
            onClick={() => navigate("/forgot-password")}
          >
            <KeyRound className="w-4 h-4 ms-2" />
            تغيير كلمة المرور
          </Button>
        </div>
      </section>
    </PageShell>
  );
};

export default Account;
